import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { RotateCcw } from 'lucide-react';
import { useSession } from '../lib/auth/session.js';
import { authApi, type CaptchaChallenge } from '../lib/api/auth.js';

const pad = (value: number): string => String(value).padStart(2, '0');

/** "23h 12m" / "4m 05s" / "42s" — a 24h ban must not read "Try again in 86399s". */
function formatCooldown(totalSeconds: number): string {
  if (totalSeconds >= 3600) {
    return `${Math.floor(totalSeconds / 3600)}h ${pad(Math.floor((totalSeconds % 3600) / 60))}m`;
  }
  if (totalSeconds >= 60) return `${Math.floor(totalSeconds / 60)}m ${pad(totalSeconds % 60)}s`;
  return `${totalSeconds}s`;
}

/** Centred password gate on the onboarding panda/green scene (see `.login`
 *  in app.css) — the card keeps the wizard's solid-fill legibility. */
export function LoginScreen() {
  const { state, loginError, rateLimitedUntil, login } = useSession();
  const [password, setPassword] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const captchaInputRef = useRef<HTMLInputElement>(null);

  const reloadCaptcha = useCallback(() => {
    setCaptchaAnswer('');
    authApi
      .captcha()
      .then(setCaptcha)
      .catch(() => setCaptcha(null));
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Fresh challenge on mount and after any failed attempt (server-side
  // challenges are single-use).
  useEffect(() => {
    reloadCaptcha();
  }, [reloadCaptcha, loginError]);

  // Live countdown while the server is throttling this client.
  useEffect(() => {
    if (rateLimitedUntil === null) {
      setCooldown(0);
      return;
    }
    const tick = () => {
      setCooldown(Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000)));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [rateLimitedUntil]);

  const blocked = cooldown > 0;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submitting || blocked || password === '' || captchaAnswer === '') return;
    setSubmitting(true);
    const ok = await login(password, captcha?.id ?? '', captchaAnswer);
    setSubmitting(false);
    if (!ok) {
      setPassword('');
      reloadCaptcha();
      captchaInputRef.current?.focus();
    }
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit} className="login-card">
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ fontSize: 'var(--font-title-size)', fontWeight: 600 }}>Panda Note</div>
          <div style={{ color: 'var(--fg-muted)' }}>Enter your password to continue.</div>
        </div>

        <input
          ref={inputRef}
          className="input"
          type="password"
          value={password}
          autoComplete="current-password"
          placeholder="Password"
          disabled={submitting || blocked}
          onChange={(event) => setPassword(event.target.value)}
          aria-label="Password"
        />

        {/* Captcha shares one row with its answer field and refresh button so
            every control edge lines up; the image keeps its fixed 132×44 box. */}
        <div className="login-captcha">
          <div
            className="login-captcha-image"
            aria-label="Captcha"
            dangerouslySetInnerHTML={{ __html: captcha?.svg ?? '' }}
          />
          <input
            ref={captchaInputRef}
            className="input"
            type="text"
            value={captchaAnswer}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={8}
            placeholder="Code"
            disabled={submitting || blocked || captcha === null}
            onChange={(event) => setCaptchaAnswer(event.target.value)}
            aria-label="Captcha code"
          />
          <button
            type="button"
            className="icon-button"
            onClick={reloadCaptcha}
            disabled={submitting || blocked}
            tabIndex={-1}
            aria-label="New captcha"
            title="New captcha"
          >
            <RotateCcw size={14} strokeWidth={1.75} />
          </button>
        </div>

        {loginError && (
          <div role="alert" style={{ color: 'var(--danger)' }}>
            {loginError}
            {blocked ? ` (${formatCooldown(cooldown)})` : null}
          </div>
        )}

        <button
          className="button"
          type="submit"
          disabled={submitting || blocked || password === '' || captchaAnswer === ''}
        >
          {submitting ? 'Signing in…' : blocked ? `Try again in ${formatCooldown(cooldown)}` : 'Sign in'}
        </button>

        {state === 'checking' && (
          <div style={{ color: 'var(--fg-faint)' }}>Checking session…</div>
        )}
      </form>
    </div>
  );
}
